import { useState } from 'react';

const accordionItems = [
  {
    id: 'problem',
    label: 'Problem Scenario',
    icon: 'ri-alert-line',
    content: (
      <div className="space-y-6">
        <div>
          <p className="text-sm font-semibold text-[#0A5F5C] uppercase tracking-wider mb-1">Customer</p>
          <p className="text-[#4A5568]">Texas colocation operator (single campus, multiple halls)</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0A5F5C] uppercase tracking-wider mb-3">Issue Reported — Two Recurring Patterns</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 bg-[#F8F7F5] rounded-xl p-4">
              <div className="w-8 h-8 flex items-center justify-center bg-[#0A5F5C]/10 rounded-lg shrink-0">
                <i className="ri-map-pin-line text-[#0A5F5C]"></i>
              </div>
              <div>
                <p className="font-semibold text-[#1A2332] text-sm">Misaligned Component Positioning</p>
                <p className="text-[#4A5568] text-sm mt-1">Critical parts are not positioned based on downtime risk</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#F8F7F5] rounded-xl p-4">
              <div className="w-8 h-8 flex items-center justify-center bg-[#0A5F5C]/10 rounded-lg shrink-0">
                <i className="ri-send-plane-line text-[#0A5F5C]"></i>
              </div>
              <div>
                <p className="font-semibold text-[#1A2332] text-sm">Reactive Expediting</p>
                <p className="text-[#4A5568] text-sm mt-1">When failures occur, shipments become reactive and expensive</p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0A5F5C] uppercase tracking-wider mb-3">Parts in Scope — 5 Critical SKUs</p>
          <div className="space-y-2">
            {[
              { n: '1', name: 'UPS Power Module', detail: 'e.g., 50–100 kW module' },
              { n: '2', name: 'CRAC/CRAH Fan Motor', detail: 'EC Motor' },
              { n: '3', name: 'Generator Starter Battery / Battery Charger Unit', detail: '' },
              { n: '4', name: 'Core Network Switch Line Card / Supervisor Module', detail: '' },
              { n: '5', name: 'PDU Branch Circuit Breaker / RPP Breaker Kit', detail: '' },
            ].map((p) => (
              <div key={p.n} className="flex items-center gap-3 bg-[#F8F7F5] rounded-xl px-4 py-3">
                <span className="w-7 h-7 flex items-center justify-center bg-[#0A5F5C] text-white text-xs font-bold rounded-full shrink-0">{p.n}</span>
                <span className="text-[#1A2332] font-medium text-sm">{p.name}</span>
                {p.detail && <span className="text-[#4A5568] text-xs ml-1">({p.detail})</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'step1',
    label: 'Step 1 — Identify Critical Components',
    icon: 'ri-search-line',
    content: (
      <div className="space-y-4">
      </div>
    ),
  },
  {
    id: 'step2',
    label: 'Step 2 — Assess Readiness Gaps',
    icon: 'ri-bar-chart-grouped-line',
    content: (
      <div className="space-y-4">
      </div>
    ),
  },
  {
    id: 'step3',
    label: 'Step 3 — Coordinate & Track',
    icon: 'ri-git-branch-line',
    content: (
      <div className="space-y-4">
      </div>
    ),
  },
  {
    id: 'step4',
    label: 'Step 4 — Deliver Monthly Reporting',
    icon: 'ri-file-chart-line',
    content: (
      <div className="space-y-4">
      </div>
    ),
  },
];

export default function ReadinessLayer() {
  const [briefOpen, setBriefOpen] = useState(false);

  return (
    <section id="pilot" className="py-24 lg:py-32 bg-[#F8F7F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Pilot Program */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A2332]">
              Pilot Program
            </h2>
          </div>

          {/* Expandable Block */}
          <div className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${briefOpen ? 'border-[#0A5F5C]/30 shadow-md' : 'border-transparent'}`}>
            <button
              onClick={() => setBriefOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0A5F5C]/10 rounded-xl shrink-0">
                  <i className="ri-file-text-line text-[#0A5F5C] text-lg"></i>
                </div>
                <span className="text-[#1A2332] font-semibold text-base">Executive Readiness Brief (Illustrative)</span>
              </div>
              <div className="w-7 h-7 flex items-center justify-center shrink-0">
                <i className={`ri-arrow-down-s-line text-[#0A5F5C] text-xl transition-transform duration-300 ${briefOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${briefOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-7 pb-7 pt-1 border-t border-[#F0EFED]">
                <div className="pt-5">
                  <img
                    src="https://static.readdy.ai/image/3bf68d6c09283a962d249fcc9ea91526/718e0e99d0edf2fc9af4ce95b13058c4.png"
                    alt="BrightSync Executive Readiness Brief - Critical Component Tiering, Before vs After Readiness Comparison, Key Readiness KPIs, and Strategic Insight"
                    className="w-full h-auto rounded-xl border border-[#E2E8F0] shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
